import React, { createContext, useContext, useEffect } from 'react';
import auth0 from './auth0Client';
import pkceChallenge from 'pkce-challenge';

import { IAuth0TokenResponse } from './interfaces/auth0token.interface';
import { useLoading } from '../context/LoadingContext';
import { LOCAL_STORAGE_NAMES, ROUTES } from '../constants/constants';
import { getGraphAccessToken } from '../api/graphClient';
import { buildAuth0LoginUrl } from '../helpers/auth0AuthenticationHelper';
import { useLogout } from '../hooks/useHandleLogout';
import { AUTH0_AUDIENCE } from '../common/constants/env.constants';
import { useLanguage } from '../context/LanguageContext';
import { showToast } from '../components/utils/toast';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../hooks/useHandleLogin';
import { clearExpiryTime, isTokenExpired, saveExpiryTime } from '../helpers/tokenExpiry';
import { getForbiddenErrorType } from '../interface/errorTypes';

export interface IAuthProviderContext {
  accessToken: string | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoginInProgress: boolean;
  login: () => void;
  logout: () => void;
  handleNormalRedirect: (code: string) => void;
  handleRedirectForMacOs: () => void;
  saveTokens: (auth0TokenResponse: IAuth0TokenResponse) => void;
}

const AuthenticationContext = createContext<IAuthProviderContext>({
  accessToken: undefined,
  isLoading: false,
  isAuthenticated: false,
  isLoginInProgress: false,
  login: () => {},
  logout: () => {},
  saveTokens: () => {},
  handleNormalRedirect: () => {},
  handleRedirectForMacOs: () => {},
});

export function useAuthenticationProvider(): IAuthProviderContext {
  const { t } = useTranslation();
  const logoutMutation = useLogout();
  const platform = Office?.context?.diagnostics?.platform;
  const { language } = useLanguage();
  const loginMutation = useLogin();
  const { setIsLoading: setIsGlobalLoading } = useLoading();
  const [accessToken, setAccessToken] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoginInProgress, setIsLoginInProgress] = React.useState<boolean>(false);
  const handleTokenInitialization = async () => {
    const isLoggedIn = localStorage.getItem(`isLoggedIn`);

    if (isLoggedIn) {
      setAccessToken(isLoggedIn);
    } else {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleTokenInitialization();
  }, []);

  const saveTokens = () => {
    setAccessToken('true');
    localStorage.setItem(`isLoggedIn`, 'true');
    primeMicrosoftGraphSession();
    setIsLoading(false);
  };
  /**
   * Attempts a silent Microsoft Graph sign-in while onboarding so a user who is already signed in
   * to Microsoft finds Graph ready. It never prompts: when it fails, the reauthorization banner
   * asks for consent. Tokens themselves are always resolved on demand.
   */
  const primeMicrosoftGraphSession = async () => {
    try {
      await getGraphAccessToken();
    } catch (error) {
      console.error('Failed to establish Microsoft Graph session:', error);
    }
  };
  const clearStorage = () => {
    const keysToPreserve = new Set([
      LOCAL_STORAGE_NAMES.timer,
      LOCAL_STORAGE_NAMES.language,
      LOCAL_STORAGE_NAMES.theme,
      LOCAL_STORAGE_NAMES.appAnalyticsPermission,
      LOCAL_STORAGE_NAMES.featureSelections,
      LOCAL_STORAGE_NAMES.startModule,
      LOCAL_STORAGE_NAMES.defaultViewPreference,
      LOCAL_STORAGE_NAMES.dashboardChartVisible,
    ]);
    sessionStorage.clear();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && !keysToPreserve.has(key)) {
        localStorage.removeItem(key);
      }
    }
  };
  const performLogoutCleanup = async () => {
    clearStorage();
    /* Earlier this approach was working but as the Outlook policy changed and now it does not support this 
    I have to use iframe to redirect to auth0 logout url
    window.location.href = auth0LogoutUrl();
    */
    setIsGlobalLoading(true);
    await auth0.logout({
      logoutParams: {
        returnTo: globalThis.location.origin,
      },
    });
  };
  const logoutWithAuth0 = async () => {
    try {
      const isOrganisationSelected = localStorage.getItem(LOCAL_STORAGE_NAMES.currentOrganisation);
      if (platform !== Office.PlatformType.Mac && isOrganisationSelected) {
        await logoutMutation.mutateAsync(undefined);
      }
    } finally {
      await performLogoutCleanup();
    }
  };
  const loginWithRedirect = async () => {
    setIsLoginInProgress(true);
    const scope = 'openid profile email offline_access Mail.ReadWrite MailboxSettings.ReadWrite';
    await auth0
      .loginWithRedirect({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: scope,
          ui_locales: language,
        },
      })
      .catch((error) => {
        if (error?.error === 'cancelled') {
          showToast('error', t('common:authenticationCancelled'));
          return;
        }
        showToast('error', error.message ?? t('common:somethingWentWrong'));
      })
      .finally(() => {
        setIsLoginInProgress(false);
      });
  };
  const loginWithAuth0 = async () => {
    if (!platform) {
      showToast('error', t('common:unsupportedPlatform'), undefined, 'login-page-toast');
      return;
    }
    setIsLoginInProgress(true);
    const { code_verifier, code_challenge } = await pkceChallenge();
    sessionStorage.setItem('code_verifier', code_verifier);
    sessionStorage.setItem('code_challenge', code_challenge);

    // For MacOs we are using loginWithRedirect as modal popup is not supported
    if (platform === Office.PlatformType.Mac) {
      loginWithRedirect();
    } else {
      try {
        const auth0LoginUrl: string = buildAuth0LoginUrl(code_challenge);
        Office.context.ui.displayDialogAsync(
          auth0LoginUrl,
          { height: 60, width: 30, promptBeforeOpen: false },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const dialog = result.value;
              dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args) => {
                try {
                  const urlParams = new URLSearchParams((args as { message: string }).message);
                  const code = urlParams.get('code');
                  if (!code) {
                    throw new Error('Invalid authorization response');
                  }
                  dialog.close();
                  handleNormalRedirect(code);
                } catch (err: unknown) {
                  clearStorage();
                  showToast(
                    'error',
                    (err as { message?: string })?.message ?? t('common:somethingWentWrong'),
                  );
                  dialog.close();
                } finally {
                  setIsLoginInProgress(false);
                }
              });
              dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                setIsLoginInProgress(false);
              });
            } else {
              setIsLoginInProgress(false);
              showToast('error', t('common:somethingWentWrong'));
            }
          },
        );
      } catch (error: unknown) {
        setIsLoginInProgress(false);
        showToast(
          'error',
          (error as { message?: string })?.message ?? t('common:somethingWentWrong'),
        );
      }
    }
  };
  const handleRedirectForMacOs = async () => {
    try {
      await auth0.handleRedirectCallback();
      const token = await auth0.getTokenSilently({
        authorizationParams: {
          audience: AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access Mail.ReadWrite MailboxSettings.ReadWrite',
        },
        detailedResponse: true,
      });
      if (token) {
        saveTokens();
      }
    } catch (error: unknown) {
      clearStorage();
      if ((error as { error?: string }).error === 'cancelled') {
        showToast('error', t('common:authenticationCancelled'));
      } else {
        showToast('error', t('common:somethingWentWrong'));
        globalThis.location.replace('/taskpane.html');
      }
    }
  };
  const handleNormalRedirect = async (code: string) => {
    const codeVerifier = sessionStorage.getItem('code_verifier');
    if (isTokenExpired()) {
      clearExpiryTime();
      loginMutation.mutateAsync(
        {
          authorizationCode: code,
          codeVerifier: codeVerifier as string,
          redirectUrl: globalThis.location.origin + ROUTES.TASKPANE_AUTH_REDIRECTION,
        },
        {
          onSuccess: (data) => {
            saveExpiryTime(data?.expirationTime);
            saveTokens();
          },
          onError: (error) => {
            const apiErrorType = error?.response?.data?.type;
            const errorType = getForbiddenErrorType(apiErrorType);
            sessionStorage.setItem('forbiddenErrorType', errorType);
            globalThis.location.href = `${globalThis.location.origin}${ROUTES.ACCESS_FORBIDDEN}`;
          },
        },
      );
    } else {
      saveTokens();
    }
  };

  return {
    isLoginInProgress: isLoginInProgress,
    accessToken: accessToken,
    saveTokens: saveTokens,
    isLoading: isLoading,
    isAuthenticated: Boolean(accessToken),
    login: () => {
      loginWithAuth0();
    },
    logout: () => {
      logoutWithAuth0();
    },
    handleNormalRedirect: (code) => {
      handleNormalRedirect(code);
    },
    handleRedirectForMacOs: () => {
      handleRedirectForMacOs();
    },
  };
}

export const useAuthentication = () => useContext(AuthenticationContext);

export const AuthenticationProvider = ({ children }: { children: React.ReactNode }) => {
  const service = useAuthenticationProvider();

  return (
    <AuthenticationContext.Provider value={service} key={'auth-provider'}>
      {children}
    </AuthenticationContext.Provider>
  );
};
